import { useEffect, useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import { X, Bug } from 'lucide-react';
import { ModalRoot } from '@/components/ui/ModalRoot';
import { Z_DIALOGS_HIGH } from '@/lib/zIndex';
import {
  submitBugReport,
  getOrCreateTesterPseudonym,
  setTesterPseudonym,
} from '@/lib/errorLogger';
import { useGameStore } from '@/state/gameStore';

interface BugReportModalProps {
  open: boolean;
  onClose: () => void;
}

const MIN_LEN = 10;

/**
 * Phase 15.3.2 — User-getriggerter Bug-Report.
 *
 * Felder:
 *   - Was sollte passieren? (Pflicht, ≥ 10 Zeichen)
 *   - Was ist tatsächlich passiert? (Pflicht, ≥ 10 Zeichen)
 *   - Schritte zum Reproduzieren (optional)
 *   - Tester-Pseudonym (Pflicht, default aus localStorage)
 *   - Toggle "Spielstand mitschicken" (default: true)
 *   - Toggle "Letzte Logs mitschicken" (default: true)
 */
export function BugReportModal({ open, onClose }: BugReportModalProps) {
  const pushToast = useGameStore((s) => s.pushToast);

  const [pseudonym, setPseudonym] = useState('');
  const [expected, setExpected] = useState('');
  const [actual, setActual] = useState('');
  const [steps, setSteps] = useState('');
  const [includeGameState, setIncludeGameState] = useState(true);
  const [includeLogs, setIncludeLogs] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // Beim Öffnen Pseudonym aus localStorage laden (oder neu generieren).
  useEffect(() => {
    if (!open) return;
    setPseudonym((cur) => (cur ? cur : getOrCreateTesterPseudonym()));
  }, [open]);

  const valid =
    pseudonym.trim().length >= 3 &&
    expected.trim().length >= MIN_LEN &&
    actual.trim().length >= MIN_LEN;

  const handleSubmit = async () => {
    if (!valid || submitting) return;
    setSubmitting(true);
    try {
      setTesterPseudonym(pseudonym.trim());
      const { trackingId } = await submitBugReport({
        pseudonym: pseudonym.trim(),
        expected: expected.trim(),
        actual: actual.trim(),
        steps: steps.trim() || undefined,
        includeGameState,
        includeLogs,
      });
      pushToast({
        kind: 'success',
        text: `Danke! Bug wurde übermittelt. Tracking-ID: ${trackingId}`,
      });
      // Modal schließt nach 2s
      setTimeout(() => {
        onClose();
        // Felder reset
        setExpected('');
        setActual('');
        setSteps('');
      }, 2000);
    } catch {
      pushToast({
        kind: 'error',
        text: 'Übermittlung fehlgeschlagen. Bitte später erneut versuchen.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <ModalRoot
          onClose={onClose}
          zIndex={Z_DIALOGS_HIGH}
          contentClass="w-full max-w-xl rounded-lg border border-border-strong bg-bg-secondary"
        >
          <header className="flex items-center justify-between border-b border-border-subtle bg-amber-500/10 px-5 py-4">
            <div className="flex items-center gap-2">
              <Bug className="h-5 w-5 text-amber-400" aria-hidden />
              <p className="font-display text-lg font-semibold">Bug melden</p>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Schließen"
              className="text-fg-muted transition hover:text-fg-primary"
            >
              <X className="h-4 w-4" aria-hidden />
            </button>
          </header>

          <div className="space-y-4 px-5 py-4 text-sm">
            <Field label="Was sollte passieren?" required hint={`mind. ${MIN_LEN} Zeichen`}>
              <textarea
                value={expected}
                onChange={(e) => setExpected(e.target.value)}
                rows={3}
                data-testid="bug-expected"
                className="w-full resize-y rounded-md border border-border-subtle bg-bg-primary p-2 text-sm text-fg-primary outline-none transition focus:border-accent-dispatch"
                placeholder="z. B. Mission sollte abgeschlossen werden, wenn alle Maßnahmen grün sind"
              />
            </Field>

            <Field
              label="Was ist tatsächlich passiert?"
              required
              hint={`mind. ${MIN_LEN} Zeichen`}
            >
              <textarea
                value={actual}
                onChange={(e) => setActual(e.target.value)}
                rows={3}
                data-testid="bug-actual"
                className="w-full resize-y rounded-md border border-border-subtle bg-bg-primary p-2 text-sm text-fg-primary outline-none transition focus:border-accent-dispatch"
                placeholder="z. B. Progress-Balken bleibt bei 100 % stehen, Mission schließt nicht"
              />
            </Field>

            <Field label="Schritte zum Reproduzieren" hint="optional">
              <textarea
                value={steps}
                onChange={(e) => setSteps(e.target.value)}
                rows={2}
                data-testid="bug-steps"
                className="w-full resize-y rounded-md border border-border-subtle bg-bg-primary p-2 text-sm text-fg-primary outline-none transition focus:border-accent-dispatch"
                placeholder="1. ... 2. ... 3. ..."
              />
            </Field>

            <Field label="Tester-Pseudonym" required>
              <input
                type="text"
                value={pseudonym}
                onChange={(e) => setPseudonym(e.target.value)}
                data-testid="bug-pseudonym"
                className="w-full rounded-md border border-border-subtle bg-bg-primary p-2 text-sm text-fg-primary outline-none transition focus:border-accent-dispatch"
                placeholder="Anonym-XXXX"
              />
            </Field>

            <div className="space-y-2 rounded-md border border-border-subtle bg-bg-elevated/30 p-3">
              <ToggleRow
                checked={includeGameState}
                onChange={setIncludeGameState}
                label="Spielstand-Snapshot mitschicken"
                hint="Modus, Stadt, Credits, Wachen-/Fahrzeug-/Mission-Anzahl. Keine persönlichen Daten."
              />
              <ToggleRow
                checked={includeLogs}
                onChange={setIncludeLogs}
                label="Letzte 50 Browser-Logs mitschicken"
                hint="console.log/warn/error-Ausgaben aus der aktuellen Sitzung."
              />
            </div>
          </div>

          <footer className="border-t border-border-subtle bg-bg-primary px-5 py-4">
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!valid || submitting}
              data-testid="bug-submit"
              className={[
                'w-full rounded-md px-4 py-3 text-sm font-semibold transition',
                valid && !submitting
                  ? 'bg-accent-emergency text-white shadow-md hover:bg-accent-emergency-hover'
                  : 'cursor-not-allowed bg-bg-elevated text-fg-muted',
              ].join(' ')}
            >
              {submitting ? 'Sende …' : 'Bug an Entwickler senden'}
            </button>
          </footer>
        </ModalRoot>
      )}
    </AnimatePresence>
  );
}

function Field({
  label,
  required,
  hint,
  children,
}: {
  label: string;
  required?: boolean;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="mb-1 flex items-baseline justify-between gap-2">
        <p className="text-xs font-semibold text-fg-primary">
          {label}
          {required && <span className="ml-1 text-accent-emergency">*</span>}
        </p>
        {hint && <p className="text-[10px] text-fg-muted">{hint}</p>}
      </div>
      {children}
    </div>
  );
}

function ToggleRow({
  checked,
  onChange,
  label,
  hint,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
  hint: string;
}) {
  return (
    <label className="flex cursor-pointer items-start gap-3">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-1 h-4 w-4 accent-accent-dispatch"
      />
      <div className="flex-1">
        <p className="text-xs font-semibold text-fg-primary">{label}</p>
        <p className="mt-0.5 text-[10px] leading-relaxed text-fg-muted">{hint}</p>
      </div>
    </label>
  );
}
